/**
 * Currency conversion utilities using Frankfurter API
 *
 * Frankfurter provides free, open-source currency exchange rates
 * - Daily updated rates (refreshed around 16:00 CET)
 * - No API key required
 * - Cached for 1 hour to reduce API calls
 */

import { frankfurterService } from '../services/frankfurter.service';

// Fallback static exchange rates (USD as base) in case API fails
const FALLBACK_EXCHANGE_RATES: Record<string, number> = {
  'USD': 1.0,
  'ILS': 3.62,
  'EUR': 0.92,
  'GBP': 0.79,
  'JPY': 149.50,
  'CHF': 0.88,
  'CAD': 1.35,
  'AUD': 1.52,
  'CNY': 7.24,
  'INR': 83.12,
  'KRW': 1320.50,
  'BRL': 4.97,
  'MXN': 17.15,
  'SEK': 10.38,
  'NOK': 10.72,
  'DKK': 6.87,
  'PLN': 3.98,
  'THB': 35.42,
};

// In-memory cache for exchange rates
let cachedRates: Record<string, number> | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour
const warnedKeys = new Set<string>();

/**
 * Fetch and cache exchange rates from Frankfurter API
 * @param baseCurrency - Base currency for rates (default: USD)
 */
export const fetchExchangeRates = async (baseCurrency: string = 'USD'): Promise<Record<string, number>> => {
  // Check if cache is still valid
  if (cachedRates && Date.now() - cacheTimestamp < CACHE_DURATION) {
    return cachedRates;
  }

  try {
    const data = await frankfurterService.getLatestRates(baseCurrency);
    cachedRates = data.rates;
    cacheTimestamp = Date.now();
    console.log(`Exchange rates updated from Frankfurter API (base: ${baseCurrency})`);
    return cachedRates;
  } catch (error) {
    console.error('Failed to fetch exchange rates, using fallback rates:', error);
    return FALLBACK_EXCHANGE_RATES;
  }
};

/**
 * Convert an amount from one currency to another using live rates
 * @param amount - The amount to convert
 * @param fromCurrency - Source currency code (e.g., 'CHF')
 * @param toCurrency - Target currency code (e.g., 'USD')
 * @param rates - Optional pre-fetched rates (for batch conversions)
 * @returns Converted amount
 */
export const convertCurrency = async (
  amount: number,
  fromCurrency: string,
  toCurrency: string = 'USD',
  rates?: Record<string, number>
): Promise<number> => {
  const from = fromCurrency.toUpperCase();
  const to = toCurrency.toUpperCase();

  if (from === to) return amount;

  try {
    // Use provided rates or fetch new ones
    const exchangeRates = rates || await fetchExchangeRates(from);

    const rate = exchangeRates[to];

    if (!rate) {
      const warningKey = `${from}-${to}-async`;
      if (!warnedKeys.has(warningKey)) {
        warnedKeys.add(warningKey);
        console.warn(`Exchange rate not found for ${to}. Using 1:1 conversion.`);
      }
      return amount;
    }

    return amount * rate;
  } catch (error) {
    console.error('Error converting currency:', error);
    return amount; // Return original amount on error
  }
};

/**
 * Synchronous version using cached rates (for immediate UI updates)
 * Falls back to static rates if cache is empty
 */
export const convertCurrencySync = (
  amount: number,
  fromCurrency: string,
  toCurrency: string = 'USD'
): number => {
  const from = fromCurrency.toUpperCase();
  const to = toCurrency.toUpperCase();

  if (from === to) return amount;

  // Use cached rates or fallback to static rates
  const rates = cachedRates || FALLBACK_EXCHANGE_RATES;

  // If we need to convert from non-USD base, we need to calculate
  if (from !== 'USD') {
    const fromRate = rates[from];
    const toRate = rates[to];

    if (!fromRate || !toRate) {
      const warningKey = `${from}-${to}-sync`;
      if (!warnedKeys.has(warningKey)) {
        warnedKeys.add(warningKey);
        console.warn(`Exchange rate not found for ${from} or ${to}. Using 1:1 conversion.`);
      }
      return amount;
    }

    // Convert to USD first, then to target currency
    const amountInUSD = amount / fromRate;
    return amountInUSD * toRate;
  }

  const rate = rates[to];
  if (!rate) {
    const warningKey = `${from}-${to}-sync`;
    if (!warnedKeys.has(warningKey)) {
      warnedKeys.add(warningKey);
      console.warn(`Exchange rate not found for ${to}. Using 1:1 conversion.`);
    }
    return amount;
  }

  return amount * rate;
};

/**
 * Clear the exchange rate cache (call when user changes base currency)
 */
export const clearExchangeRateCache = (): void => {
  cachedRates = null;
  cacheTimestamp = 0;
  warnedKeys.clear();
  frankfurterService.clearCache();
};

/**
 * Group expenses by currency and calculate totals
 * @param expenses - Array of expenses with currency and amount
 * @returns Array of currency totals
 */
export interface CurrencyTotal {
  currency: string;
  amount: number;
}

export const groupExpensesByCurrency = (
  expenses: Array<{ currency: string; amount: number }>
): CurrencyTotal[] => {
  const currencyMap = new Map<string, number>();

  expenses.forEach(expense => {
    const currency = expense.currency.toUpperCase();
    const currentTotal = currencyMap.get(currency) || 0;
    currencyMap.set(currency, currentTotal + expense.amount);
  });

  return Array.from(currencyMap.entries())
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => b.amount - a.amount); // Sort by amount descending
};

/**
 * Calculate total expenses in a target currency from multiple currencies
 * @param expenses - Array of expenses with currency and amount
 * @param targetCurrency - Target currency code (default: USD)
 * @returns Total amount in target currency
 */
export const calculateTotalInCurrency = async (
  expenses: Array<{ currency: string; amount: number }>,
  targetCurrency: string = 'USD'
): Promise<number> => {
  // Fetch rates once for all conversions
  const rates = await fetchExchangeRates('USD');

  let total = 0;

  for (const expense of expenses) {
    const converted = await convertCurrency(
      expense.amount,
      expense.currency,
      targetCurrency,
      rates
    );
    total += converted;
  }

  return total;
};

/**
 * Synchronous version - Calculate total using cached rates
 * @param expenses - Array of expenses with currency and amount
 * @param targetCurrency - Target currency code (default: USD)
 * @returns Total amount in target currency
 */
export const calculateTotalInCurrencySync = (
  expenses: Array<{ currency: string; amount: number }>,
  targetCurrency: string = 'USD'
): number => {
  return expenses.reduce((total, expense) => {
    const amountInTarget = convertCurrencySync(expense.amount, expense.currency, targetCurrency);
    return total + amountInTarget;
  }, 0);
};

// Backward compatibility alias
export const calculateTotalInUSD = calculateTotalInCurrencySync;
